import React, { useEffect, useMemo, useState,useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  PieChart, Area, AreaChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid
} from 'recharts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Colors for charts
const COLORS = ['#272757', '#20B2AA', '#f59e0b', '#4A0404', '#10b981', '#6366f1', '#f97316', '#8b5cf6', '#06b6d4', '#14b8a6'];

// Month maps
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_SHORT = MONTH_NAMES.map(m => m.slice(0,3).toUpperCase());
const MONTH_MAP = {
  'JANUARY': 0,'JAN':0,'FEBRUARY':1,'FEB':1,'MARCH':2,'MAR':2,'APRIL':3,'APR':3,
  'MAY':4,'JUNE':5,'JUN':5,'JULY':6,'JUL':6,'AUGUST':7,'AUG':7,'SEPTEMBER':8,'SEP':8,'SEPT':8,
  'OCTOBER':9,'OCT':9,'NOVEMBER':10,'NOV':10,'DECEMBER':11,'DEC':11,'DEC':11
};

// targeted parser for "13-NOVEMBER-2025" (also accepts "13-november-2025" or "13-NOV-2025" or "13 NOVEMBER 2025")
function parseMonthIndexFromEnrolledBatch_ExactFormat(raw) {
  if (!raw && raw !== 0) return null;
  const s = String(raw).trim();
  if (!s) return null;

  const tokens = s.split(/[-\s\/]+/).filter(Boolean);
  if (tokens.length < 2) return null;

  // prefer middle token if format is exactly 3 tokens
  let monthToken = null;
  if (tokens.length === 3) monthToken = tokens[1];
  else {
    monthToken = tokens.find(t => MONTH_MAP.hasOwnProperty(t.toString().toUpperCase()));
  }
  if (!monthToken) return null;
  const idx = MONTH_MAP[monthToken.toString().toUpperCase()];
  return typeof idx === 'number' ? idx : null;
}

// helper: extract year from EnrolledBatch like "13-NOVEMBER-2025"
function parseYearFromEnrolledBatch(raw) {
  if (!raw) return null;
  const s = String(raw);
  const m = s.match(/\b(20\d{2})\b/);
  if (m) return parseInt(m[1], 10);
  return null;
}

// robust amount parsing helper: strips commas, currency symbols, returns float
function parseAmount(v) {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  let s = String(v).trim();
  // remove non-digit except dot and minus
  s = s.replace(/[₹,$\s]/g, '').replace(/,/g, '');
  // keep digits, dot, minus
  s = s.replace(/[^0-9.\-]/g, '');
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

// ---------------- CSV helpers ----------------
function safeString(v) {
  if (v === null || v === undefined) return '';
  return String(v).replace(/\"/g, '""'); // escape double quotes by doubling
}

function buildCSV(rows, headers) {
  // rows: array of objects, headers: array of { key, label }
  const headerLine = headers.map(h => `"${h.label.replace(/\"/g, '""')}"`).join(',');
  const lines = [headerLine];
  rows.forEach(r => {
    const line = headers.map(h => `"${safeString(r[h.key] ?? '')}"`).join(',');
    lines.push(line);
  });
  return lines.join('\n');
}

function downloadCSV(filename, csvContent) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function DashboardFirstInstallment() {
  const navigate = useNavigate();
  const location = useLocation();

  const dashboardRef = useRef(null); // attach to container to capture to dowmload pdf,png



  const token = localStorage.getItem('token');
  const email = localStorage.getItem('email');
  const role = localStorage.getItem('role');

  // lead states
  const [students, setStudents] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [filterMonth, setFilterMonth] = useState('ALL'); // 'ALL' or 0..11
  const [filterYear, setFilterYear] = useState('ALL');   // 'ALL' or 2025 etc.
  const [filterRole, setFilterRole] = useState('ALL');   // 'ALL' | 'STUDENT' | 'PROFESSIONAL'
  const [filterFirstInstallment, setFilterFirstInstallment] = useState('ALL'); // 'ALL'|'PAID'|'UNPAID'
  const [q, setQ] = useState(''); // free-text search across name/email/phone

  // verify user effect (kept minimal)
  useEffect(() => {
    const verify = async () => {
      if (!token) return navigate('/login');
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/check-user`, { email, role, token });
        if (!res?.data?.success) {
          toast.error(res?.data?.message || 'Please login again');
          localStorage.clear();
          return navigate('/login');
        }
      } catch (err) {
        toast.error('Please login again');
        localStorage.clear();
        navigate('/login');
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname]);

  // classifyLead unchanged (keeps previous heuristics)
  const classifyLead = (r) => {
    if (!r || typeof r !== 'object') return 'professional';
    const toStr = (v) => (v === null || v === undefined ? '' : String(v).trim().toUpperCase());
    const fieldsToCheck = [
      r.LeadCategory, r.category, r.Type, r.type, r.role, r.Role, r.leadType, r.LeadType, r.LeadFor, r.lead_for,
      r.profile?.role, r.isStudent, r.isProfessional, r.student, r.professional, r.designation, r.title
    ];

    if (r.isStudent === true || r.student === true) return 'student';
    if (r.isProfessional === true || r.professional === true) return 'professional';

    const combined = fieldsToCheck.map(toStr).join(' ');
    if (combined.includes('STUDENT') || combined.includes('LEARNER') || combined.includes('FRESHER') || combined.includes('TRAINING')) return 'student';
    if (combined.includes('PROFESSIONAL') || combined.includes('WORK') || combined.includes('EMPLOYEE') || combined.includes('EXPERIENCED') || combined.includes('EMP') || combined.includes('PRO')) return 'professional';

    const emailStr = toStr(r.Email || r.email || r.contactEmail || r.contact_email || '');
    if (emailStr && (emailStr.includes('.edu') || emailStr.includes('student'))) return 'student';
    return 'professional';
  };

  // fetch leads
  useEffect(() => {
    const fetchAll = async () => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/firstinstallment/all-first-installment-leads`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const raw = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
        const arr = Array.isArray(raw) ? raw : [raw];

        const s = [];
        const p = [];

        arr.forEach((item, i) => {
          const key = item.Email ?? item.email ?? item._id ?? item.id ?? `lead-${i}`;
          const lead = { key, ...item };
          const cls = classifyLead(item);
          if (cls === 'student') s.push(lead);
          else p.push(lead);
        });

        setStudents(s.map((r, i) => ({ key: r.key ?? `s-${i}`, ...r })));
        setProfessionals(p.map((r, i) => ({ key: r.key ?? `p-${i}`, ...r })));
      } catch (err) {
        console.error(err);
        setError('Failed to fetch demo leads');
        toast.error('Failed to load demo leads');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    // run when location.key changes (manual refresh or navigation)
  }, [token, location.key]);

  // combinedLeads (unfiltered)
  const combinedLeads = useMemo(() => [...students, ...professionals], [students, professionals]);

  // derive available years from combinedLeads for the year dropdown
  const availableYears = useMemo(() => {
    const sety = new Set();
    combinedLeads.forEach(r => {
      const y = parseYearFromEnrolledBatch(r.EnrolledBatch ?? r.enrolledBatch ?? r.availabilityBatch ?? r.availability_batch ?? '');
      if (y) sety.add(y);
      else {
        // fallback to createdAt year
        const cr = r.createdAt ?? r.created_at ?? r.created;
        if (cr) {
          const d = new Date(cr);
          if (!isNaN(d)) sety.add(d.getFullYear());
        }
      }
    });
    return Array.from(sety).sort((a,b) => b-a);
  }, [combinedLeads]);

  // filteredLeads pipeline
  const filteredLeads = useMemo(() => {
    const qLower = (q || '').trim().toLowerCase();

    const getMonthYear = (r) => {
      const raw = r.EnrolledBatch ?? r.enrolledBatch ?? r.availabilityBatch ?? r.availability_batch ?? '';
      const monthIdx = parseMonthIndexFromEnrolledBatch_ExactFormat(raw);
      let year = parseYearFromEnrolledBatch(raw);
      if (!year) {
        const created = r.createdAt ?? r.created_at ?? r.created;
        if (created) {
          const d = new Date(created);
          if (!isNaN(d)) year = d.getFullYear();
        }
      }
      return { monthIdx, year };
    };

    return combinedLeads.filter((r) => {
      // MONTH filter
      if (filterMonth !== 'ALL') {
        const { monthIdx } = getMonthYear(r);
        if (monthIdx !== filterMonth) return false;
      }

      // YEAR filter
      if (filterYear !== 'ALL') {
        const { year } = getMonthYear(r);
        if (year !== filterYear) return false;
      }

      // ROLE filter
      if (filterRole !== 'ALL') {
        const cls = classifyLead(r);
        if (filterRole === 'STUDENT' && cls !== 'student') return false;
        if (filterRole === 'PROFESSIONAL' && cls !== 'professional') return false;
      }

      // FIRST INSTALLMENT filter
      if (filterFirstInstallment !== 'ALL') {
        const raw = String(r.FirstInstallmentStatus ?? r.firstInstallmentStatus ?? r.first_installment_status ?? '').trim().toUpperCase();
        const isPaid = /\bPAID\b/.test(raw);
        const isUnpaid = /\bUNPAID\b/.test(raw) || raw === '';
        if (filterFirstInstallment === 'PAID' && !isPaid) return false;
        if (filterFirstInstallment === 'UNPAID' && !isUnpaid) return false;
      }

      // TEXT search across a few common fields
      if (qLower) {
        const hay = [
          r.FullName ?? r.fullName ?? r.name ?? '',
          r.Email ?? r.email ?? '',
          r.PhoneNumber ?? r.phone ?? r.mobile ?? '',
          r.LeadSource ?? r.source ?? ''
        ].join(' ').toLowerCase();
        if (!hay.includes(qLower)) return false;
      }

      return true;
    });
  }, [combinedLeads, filterMonth, filterYear, filterRole, filterFirstInstallment, q]);

  // ------------- CSV export implementation -------------
  const csvHeaders = [
    { key: 'FullName', label: 'FullName' },
    { key: 'Email', label: 'Email' },
    { key: 'PhoneNumber', label: 'PhoneNumber' },
    { key: 'LeadCategory', label: 'LeadCategory' },
    { key: 'LeadDate', label: 'LeadDate' },
    { key: 'LeadSource', label: 'LeadSource' },
    { key: 'EnrolledBatch', label: 'EnrolledBatch' },
    { key: 'BatchDay', label: 'BatchDay' },
    { key: 'EnrollmentType', label: 'EnrollmentType' },
    { key: 'NegotiatedFee', label: 'NegotiatedFee' },
     { key: 'Installment', label: 'Installment' },
    { key: 'CallStatus', label: 'CallStatus' },
    { key: 'Enrolled', label: 'Enrolled' },
    { key: 'FirstInstallmentStatus', label: 'FirstInstallmentStatus' },
    { key: 'FirstInstallmentDate', label: 'FirstInstallmentDate' },
    { key: 'AmountPaid', label: 'AmountPaid' },
    { key: 'JoiningMessage', label: 'JoiningMessage' },
  ];

  const transformLeadForCSV = (r) => ({
    FullName: r.FullName ?? r.fullName ?? r.name ?? '',
    Email: r.Email ?? r.email ?? '',
    PhoneNumber: r.PhoneNumber ?? r.phone ?? r.mobile ?? r.contact ?? '',
    LeadCategory : r.LeadCategory,
    LeadDate : r.LeadDate,
     LeadSource: r.LeadSource ?? r.leadSource ?? r.source ?? r.Source ?? '',
         EnrolledBatch: r.EnrolledBatch ?? r.enrolledBatch ?? r.availabilityBatch ?? r.availability_batch ?? '',
         BatchDay:r.BatchDay,
    EnrollmentType: r.EnrollmentType ?? r.enrollmentType ?? r.enrollment_type ?? r.Type ?? r.type ?? '',
    NegotiatedFee: (r.NegotiatedFee ?? r.negotiatedFee ?? r.TotalFee ?? r.totalFee ?? r.fee ?? r.Fee ?? '').toString(),
    Installment : r.Installment,
    CallStatus : r.CallStatus,
    Enrolled : r.Enrolled,
    FirstInstallmentStatus : r.FirstInstallmentStatus,
    FirstInstallmentDate: r.FirstInstallmentDate,
    AmountPaid : r.AmountPaid,
    JoiningMessage : r.JoiningMessage,
   
  });

  const exportFilteredLeadsCSV = () => {
    try {
      const rows = filteredLeads.map(transformLeadForCSV);
      const csv = buildCSV(rows, csvHeaders);

      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, '-');
      const filename = `first-installments-${timestamp}.csv`;
      downloadCSV(filename, csv);
      toast.success('CSV exported successfully');
    } catch (err) {
      console.error('CSV export failed', err);
      toast.error('Failed to export CSV');
    }
  };

    // Export PNG
const exportFilteredLeadsPNG = async () => {
  if (!dashboardRef.current) {
    toast.error('Dashboard not ready to export');
    return;
  }
  try {
    // scale for better resolution
    const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true, allowTaint: true });
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `first_installment_${(new Date()).toISOString().slice(0,10)}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('PNG exported');
  } catch (err) {
    console.error(err);
    toast.error('Failed to export PNG');
  }
};

// Export PDF (multi-page aware)
const exportFilteredLeadsPDF = async () => {
  if (!dashboardRef.current) {
    toast.error('Dashboard not ready to export');
    return;
  }
  try {
    // render to canvas at higher scale
    const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true, allowTaint: true });
    const imgData = canvas.toDataURL('image/png');

    // A4 size in pt (points) for jsPDF
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // compute image dims to fit page width
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    if (imgHeight <= pageHeight) {
      // single page
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    } else {
      // multi-page: split vertically
      let remainingHeight = imgHeight;
      let position = 0;
      const canvasPageHeight = (canvas.height / imgHeight) * pageHeight; // approximate page height in canvas px

      // We'll slice the full-size canvas into pages by drawing portions to a temporary canvas
      const renderedCanvas = canvas;
      const scale = imgWidth / canvas.width; // scale factor used for img in PDF
      const pageCanvas = document.createElement('canvas');
      const pageCtx = pageCanvas.getContext('2d');

      const pageCanvasHeightPx = Math.floor(pageHeight / scale); // how many source px fit per pdf page

      pageCanvas.width = canvas.width;
      pageCanvas.height = pageCanvasHeightPx;

      let srcY = 0;
      while (srcY < renderedCanvas.height) {
        // draw portion
        pageCtx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageCtx.drawImage(renderedCanvas, 0, srcY, renderedCanvas.width, pageCanvas.height, 0, 0, pageCanvas.width, pageCanvas.height);
        const pageDataUrl = pageCanvas.toDataURL('image/png');

        if (srcY > 0) pdf.addPage();
        pdf.addImage(pageDataUrl, 'PNG', 0, 0, imgWidth, (pageCanvas.height * imgWidth) / pageCanvas.width);

        srcY += pageCanvas.height;
      }
    }

    const fileName = `first_installment_${(new Date()).toISOString().slice(0,10)}.pdf`;
    pdf.save(fileName);
    toast.success('PDF exported');
  } catch (err) {
    console.error(err);
    toast.error('Failed to export PDF');
  }
};

  // ---------------- rest of derived data (unchanged) ----------------
  // Top sources (from filtered leads)
  const topSources = useMemo(() => {
    const counts = {};
    const normalize = (v) => (v || '').toString().trim().toUpperCase();
    filteredLeads.forEach((r) => {
      const raw = r.LeadSource || r.leadSource || r.source || r.Source || r.lead_source || r.SourceOfLead || '';
      const key = normalize(raw) || 'UNKNOWN';
      counts[key] = (counts[key] || 0) + 1;
    });

    const sorted = Object.entries(counts)
      .map(([name, cnt]) => ({ name, count: cnt }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return sorted;
  }, [filteredLeads]);

  // Student vs Professional donut data (filtered)
  const typeBreakdown = useMemo(() => {
    const st = filteredLeads.filter(l => classifyLead(l) === 'student').length;
    const pr = filteredLeads.length - st;
    return [
      { name: 'Students', value: st },
      { name: 'Professionals', value: pr },
    ];
  }, [filteredLeads]);

  // Batch day aggregation (filtered)
  const batchDayData = useMemo(() => {
    const counts = { MORNING: 0, EVENING: 0, UNKNOWN: 0 };
    filteredLeads.forEach((r) => {
      const raw = r.BatchDay || r.batchDay || r.batch_day || r.batch || r.AvailabilityBatch || '';
      const key = (raw || '').toString().trim().toUpperCase();

      if (!key) counts.UNKNOWN += 1;
      else if (key.includes('MORN')) counts.MORNING += 1;
      else if (key.includes('EVE')) counts.EVENING += 1;
      else counts.UNKNOWN += 1;
    });

    const arr = [
      { name: 'MORNING', count: counts.MORNING },
      { name: 'EVENING', count: counts.EVENING },
    ];
    if (counts.UNKNOWN > 0) arr.push({ name: 'UNKNOWN', count: counts.UNKNOWN });
    return arr;
  }, [filteredLeads]);

  // Enrollment Type
  const enrollmentTypeData = useMemo(() => {
    const counts = { 'JUNIOR PENETRATION TESTER': 0, 'SENIOR PENETRATION TESTER': 0 };
    filteredLeads.forEach((r) => {
      const raw = (r.EnrollmentType || r.enrollmentType || r.enrollment_type || '').toString().trim().toUpperCase();
      if (raw.includes('JUNIOR')) counts['JUNIOR PENETRATION TESTER'] += 1;
      else if (raw.includes('SENIOR')) counts['SENIOR PENETRATION TESTER'] += 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [filteredLeads]);

  // Call Status
  const callStatusData = useMemo(() => {
    const counts = { REQUIRED: 0, PERFORMED: 0, "NOT REQUIRED": 0};
    filteredLeads.forEach((r) => {
      const raw = (r.CallStatus || r.callStatus || r.call_status || '').toString().trim().toUpperCase();
      if (raw.includes('REQUIRED')) counts.REQUIRED += 1;
      else if (raw.includes('PERFORMED') && !raw.includes('NOT')) counts.PERFORMED += 1;
      else if (raw.includes('NOT REQUIRED')) counts["NOT REQUIRED"] += 1;
      else counts.REQUIRED += 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [filteredLeads]);

  // Payment Link
  const paymentLinkData = useMemo(() => {
    const counts = { PENDING: 0, SENT: 0 };
    filteredLeads.forEach((r) => {
      const raw = (r.PaymentLink_1 || '').toString().trim().toUpperCase();
      if (raw.includes('PENDING')) counts.PENDING += 1;
      else if (raw.includes('SENT')) counts.SENT += 1;
      else counts.PENDING += 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [filteredLeads]);

  // First Installment (PAID / UNPAID)
  const firstInstallmentData = useMemo(() => {
    const counts = { PAID: 0, UNPAID: 0 };
    filteredLeads.forEach((r) => {
      const raw = (r.FirstInstallmentStatus || r.firstInstallmentStatus || r.first_installment_status || '').toString().trim().toUpperCase();
      if (/^\bUNPAID\b$/.test(raw) || raw.includes('UNPAID') || raw === '') counts.UNPAID += 1;
      else if (/^\bPAID\b$/.test(raw) || raw.includes('PAID')) counts.PAID += 1;
      else counts.UNPAID += 1;
    });
    const total = counts.PAID + counts.UNPAID || 1;
    const paidPct = Math.round((counts.PAID / total) * 100);
    const unpaidPct = 100 - paidPct;
    const stacked = [{ name: 'First Installment', PAID: counts.PAID, UNPAID: counts.UNPAID, paidPct, unpaidPct, total }];
    return { counts, stacked, paidPct, unpaidPct, total };
  }, [filteredLeads]);

  // Enrolled Status
  const enrolledStatusData = useMemo(() => {
    const counts = { PENDING: 0, YES: 0, NO: 0 };
    const normalize = (v) => {
      if (v === null || v === undefined) return '';
      if (typeof v === 'boolean') return v ? 'YES' : 'NO';
      return String(v).trim().toUpperCase();
    };

    filteredLeads.forEach((r) => {
      const raw = normalize(
        r.EnrolledStatus ||
        r.enrolledStatus ||
        r.enrolled_status ||
        r.Enrolled ||
        r.enrolled ||
        r.enrollment_status ||
        r.EnrollmentStatus ||
        r.Status ||
        r.status ||
        ''
      );

      if (/\bPENDING\b/.test(raw) || raw === '' || raw === 'PEND') counts.PENDING += 1;
      else if (/\bYES\b/.test(raw) || raw === 'Y' || raw === 'TRUE' || raw.includes('PAID') || raw.includes('ENROLLED')) counts.YES += 1;
      else if (/\bNO\b/.test(raw) || raw === 'N' || raw === 'FALSE' || raw.includes('NOT') || raw.includes('DECLINED')) counts.NO += 1;
      else counts.PENDING += 1;
    });

    const total = counts.PENDING + counts.YES + counts.NO || 1;
    const pct = (v) => Math.round((v / total) * 100);
    const pie = [
      { name: 'Pending', value: counts.PENDING },
      { name: 'Yes', value: counts.YES },
      { name: 'No', value: counts.NO },
    ];
    const stacked = [{ name: 'Enrolled', NO: counts.NO, PENDING: counts.PENDING, YES: counts.YES, total }];
    return { counts, total, pctPending: pct(counts.PENDING), pctYes: pct(counts.YES), pctNo: pct(counts.NO), pie, stacked };
  }, [filteredLeads]);

  // Joining Messages
  const joiningMessageElegant = useMemo(() => {
    let pending = 0;
    let sent = 0;
    filteredLeads.forEach((r) => {
      const raw = (r.JoiningMessage || r.joiningMessage || r.joining_message || '').toString().trim().toUpperCase();
      if (raw === '') pending += 1;
      else if (/\bSENT\b/.test(raw)) sent += 1;
      else if (/\bPENDING\b/.test(raw)) pending += 1;
      else pending += 1;
    });
    const total = Math.max(1, pending + sent);
    const pctSent = Math.round((sent / total) * 100);
    const pctPending = 100 - pctSent;
    return {
      series: [{ name: 'Pending', value: pending }, { name: 'Sent', value: sent }],
      counts: { pending, sent, total },
      pct: { sent: pctSent, pending: pctPending },
    };
  }, [filteredLeads]);

  // --- Month-wise buckets (Jan..Dec) derived from filteredLeads using EnrolledBatch exact parser ---
  const monthBuckets = useMemo(() => {
    const counts = new Array(12).fill(0);
    const revenue = new Array(12).fill(0);

    filteredLeads.forEach((r) => {
      const rawBatch = r.EnrolledBatch ?? r.enrolledBatch ?? r.availabilityBatch ?? r.availability_batch ?? '';
      const monthIdx = parseMonthIndexFromEnrolledBatch_ExactFormat(rawBatch);
      if (monthIdx !== null && monthIdx >= 0 && monthIdx <= 11) {
        counts[monthIdx] += 1;
        const val = parseFloat(r.NegotiatedFee ?? r.negotiatedFee ?? r.fee ?? 0);
        if (!isNaN(val) && val > 0) revenue[monthIdx] += val;
      } else {
        // no enrolled month -> ignore for monthBuckets
      }
    });

    const monthData = MONTH_SHORT.map((m, idx) => ({
      name: m,
      count: counts[idx],
      revenue: Math.round(revenue[idx] * 100) / 100
    }));

    const totalCount = monthData.reduce((s, x) => s + x.count, 0);
    const totalRevenue = monthData.reduce((s, x) => s + (x.revenue || 0), 0);

    return { monthData, counts, revenue, totalCount, totalRevenue };
  }, [filteredLeads]);

  const monthCounts = monthBuckets.monthData;
  const monthRevenueTrend = useMemo(() => {
    if (role !== 'ADMIN') return null;
    return { total: monthBuckets.totalRevenue, trend: monthBuckets.monthData.map(m => ({ month: m.name, value: m.revenue || 0 })), months: 12 };
  }, [monthBuckets, role]);

  // total pending (after filters)
  const totalPending = filteredLeads.length;

  // --- NEW: TOTAL REVENUE AGGREGATION (NegotiatedFee) based on filteredLeads ---
  const totalRevenueTotals = useMemo(() => {
    let total = 0;
    let junior = 0;
    let senior = 0;

    filteredLeads.forEach((r) => {
      const amt = parseAmount(
        r.NegotiatedFee ??
        r.Negotiated_Fee ??
        r.negotiatedFee ??
        r.negotiated_fee ??
        r.TotalFee ??
        r.totalFee ??
        r.Fee ??
        r.fee ??
        0
      );

      total += amt;

      const enrollment = (r.EnrollmentType || r.enrollmentType || r.enrollment_type || r.Type || r.type || r.Course || r.course || '')
        .toString().trim().toUpperCase();

      if (enrollment.includes('JUNIOR')) junior += amt;
      else if (enrollment.includes('SENIOR')) senior += amt;
      else {
        const text = ((r.title || r.name || r.FullName || r.comment || r.notes) || '').toString().toUpperCase();
        if (text.includes('JUNIOR')) junior += amt;
        else if (text.includes('SENIOR')) senior += amt;
      }
    });

    total = Math.round(total * 100) / 100;
    junior = Math.round(junior * 100) / 100;
    senior = Math.round(senior * 100) / 100;

    return { total, junior, senior };
  }, [filteredLeads]);

  const totalRevenueChart = useMemo(() => ([
    { name: 'Junior', amount: totalRevenueTotals.junior },
    { name: 'Senior', amount: totalRevenueTotals.senior },
  ]), [totalRevenueTotals]);

  // --- FIRST INSTALLMENT AMOUNT AGGREGATION (NEW) based on filteredLeads ---
  const firstInstallmentTotals = useMemo(() => {
    let total = 0;
    let junior = 0;
    let senior = 0;

    filteredLeads.forEach((r) => {
      const amt = parseAmount(
        r.AmountPaid ?? r.firstInstallmentAmount ?? r.first_installment_amount ??
        r.First_Installment_Amount ?? r.FirstInstallmentAmountPaid ?? r.FirstInstallment ?? r.Amount ?? r.amount ?? 0
      );

      total += amt;

      const enrollment = (r.EnrollmentType || r.enrollmentType || r.enrollment_type || r.Type || r.type || r.Course || r.course || '').toString().trim().toUpperCase();
      if (enrollment.includes('JUNIOR')) junior += amt;
      else if (enrollment.includes('SENIOR')) senior += amt;
      else {
        const text = ((r.title || r.name || r.FullName || r.comment || r.notes) || '').toString().toUpperCase();
        if (text.includes('JUNIOR')) junior += amt;
        else if (text.includes('SENIOR')) senior += amt;
      }
    });

    total = Math.round(total * 100) / 100;
    junior = Math.round(junior * 100) / 100;
    senior = Math.round(senior * 100) / 100;

    return { total, junior, senior };
  }, [filteredLeads]);

  const firstInstallmentChart = useMemo(() => ([
    { name: 'Junior', amount: firstInstallmentTotals.junior },
    { name: 'Senior', amount: firstInstallmentTotals.senior },
  ]), [firstInstallmentTotals]);

  // --- JSX return ---
  return (
    <div ref={dashboardRef} className="p-6 space-y-6">
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        className="!mb-6 !ml-4"
        toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value,10))} className="px-3 py-2 border rounded">
          <option value="ALL">All months</option>
          {MONTH_NAMES.map((m, idx) => <option key={m} value={idx}>{m}</option>)}
        </select>

        <select value={filterYear} onChange={(e) => setFilterYear(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value,10))} className="px-3 py-2 border rounded">
          <option value="ALL">All years</option>
          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="px-3 py-2 border rounded">
          <option value="ALL">All roles</option>
          <option value="STUDENT">Students</option>
          <option value="PROFESSIONAL">Professionals</option>
        </select>

        <select value={filterFirstInstallment} onChange={(e) => setFilterFirstInstallment(e.target.value)} className="px-3 py-2 border rounded">
          <option value="ALL">All payments</option>
          <option value="PAID">Paid</option>
          <option value="UNPAID">Unpaid</option>
        </select>

        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name/email/phone" className="px-3 py-2 border rounded flex-1 min-w-[220px]" />

        <button onClick={() => { setFilterMonth('ALL'); setFilterYear('ALL'); setFilterRole('ALL'); setFilterFirstInstallment('ALL'); setQ(''); }} className="px-3 py-2 rounded bg-slate-100">Reset</button>

       <div className="flex items-center gap-2 ml-auto">
          <button onClick={exportFilteredLeadsCSV} className="px-3 py-2 text-white rounded-md shadow-sm bg-emerald-600 hover:bg-emerald-700">
            Export CSV
          </button>

            {/* PNG export */}
<button onClick={exportFilteredLeadsPNG} className="px-3 py-2 ml-2 text-white rounded bg-sky-600 hover:bg-sky-700">Export PNG</button>

{/* PDF export */}
<button onClick={exportFilteredLeadsPDF} className="px-3 py-2 ml-2 text-white rounded bg-rose-600 hover:bg-rose-700">Export PDF</button>
      
     
         
        </div>
      </div>

      {/* Header / top cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex flex-col items-start justify-start col-span-1 p-4 bg-white shadow rounded-xl min-h-[220px]">
          <div className="flex items-center justify-between w-full mb-4">
            <h3 className="text-xl font-bold text-black ">TOTAL FIRST INSTALLMENT LEADS</h3>
          </div>
          <div className="flex items-center justify-center w-10 h-10 mx-auto mt-4 rounded-md">
            <div className="text-center">
              <div className="mt-5 font-bold text-7xl">{loading ? '...' : totalPending}</div>
            </div>
          </div>
        </div>

        <div className="col-span-1 p-4 bg-white shadow lg:col-span-1 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-black">LEADS SOURCES</h3>
            <button className="text-xl font-bold text-black" onClick={() => window.location.reload()}>↻</button>
          </div>

          <div className="divide-y">
            {topSources.length === 0 && <div className="p-6 text-sm text-slate-400">No sources available</div>}
            {topSources.map((s, i) => (
              <div key={s.name} className="flex items-center justify-between py-3">
                <div className="text-sm font-semibold text-black">{i + 1}. {s.name}</div>
                <div className="text-sm font-semibold text-black">{s.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insert your ADMIN totals + first-installment KPIs here */}
      {role === "ADMIN" && (
        <>
         <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <div className="col-span-1 p-4 bg-white shadow rounded-xl">
            <h3 className="mb-2 text-lg font-semibold text-black">
              TOTAL REVENUE — NEGOTIATED FEE
            </h3>

            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 text-center bg-yellow-100 rounded-md">
                <div className="text-md text-slate-600">Total Revenue</div>
                <div className="mt-2 text-3xl font-bold">
                  ₹ { (totalRevenueTotals.total || 0).toLocaleString('en-IN') }
                </div>
              </div>
              <div className="p-3 text-center rounded-md bg-slate-100">
                <div className="text-md text-slate-500">Junior Revenue</div>
                <div className="mt-2 text-2xl font-semibold">
                  ₹ { (totalRevenueTotals.junior || 0).toLocaleString('en-IN') }
                </div>
              </div>
              <div className="p-3 text-center rounded-md bg-slate-100">
                <div className="text-md text-slate-500">Senior Revenue</div>
                <div className="mt-2 text-2xl font-semibold">
                  ₹ { (totalRevenueTotals.senior || 0).toLocaleString('en-IN') }
                </div>
              </div>
            </div>

            {/* Blue Bar Chart with Labels */}
            <div className="grid grid-cols-1 gap-4 mt-4">
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={totalRevenueChart}
                    margin={{ top: 8, right: 16, bottom: 16, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="6 4" vertical={false} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(v) => [`₹ ${v.toLocaleString('en-IN')}`, "Amount"]} />
                    <Bar
                      dataKey="amount"
                      name="Amount (₹)"
                      radius={[6, 6, 0, 0]}
                      barSize={90}
                      label={{
                        position: "top",
                        formatter: (v) => `₹${v.toLocaleString('en-IN')}`,
                        fill: "#111827",
                        fontSize: 15,
                        fontWeight: 600,
                      }}
                    >
                      {totalRevenueChart.map((entry, idx) => (
                        <Cell key={`rev-${idx}`} fill="#a3030b" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* NEW: First Installment Summary (KPI) */}
          <div className="col-span-1 p-4 bg-white shadow rounded-xl">
            <h3 className="mb-2 text-lg font-semibold text-black">FIRST INSTALLMENT — AMOUNT PAID</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 text-center bg-green-200 rounded-md">
                <div className="text-md text-slate-500">Total Collected</div>
                <div className="mt-2 text-3xl font-bold">₹ { (firstInstallmentTotals.total || 0).toLocaleString('en-IN') }</div>
              </div>
              <div className="p-3 text-center rounded-md bg-slate-100">
                <div className="text-md text-slate-500">Junior</div>
                <div className="mt-2 text-2xl font-semibold">₹ { (firstInstallmentTotals.junior || 0).toLocaleString('en-IN') }</div>
              </div>
              <div className="p-3 text-center rounded-md bg-slate-100">
                <div className="text-md text-slate-500">Senior</div>
                <div className="mt-2 text-2xl font-semibold">₹ { (firstInstallmentTotals.senior || 0).toLocaleString('en-IN') }</div>
              </div>
            </div>

            {/* mini charts row */}
            <div className="grid grid-cols-1 gap-4 mt-4">
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={firstInstallmentChart} margin={{ top: 8, right: 16, bottom: 16, left: 0 }}>
                    <CartesianGrid strokeDasharray="6 4" vertical={false} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(v) => [`₹ ${v.toLocaleString('en-IN')}`, 'Amount']} />
                    <Bar
                      dataKey="amount"
                      name="Amount (₹)"
                      radius={[6, 6, 0, 0]}
                      barSize={80}
                      label={{ position: 'top', formatter: (v) => `₹${v.toLocaleString('en-IN')}`, fill: '#111827', fontSize: 15, fontWeight: 600 }}
                    >
                      {firstInstallmentChart.map((entry, idx) => (
                        <Cell key={`pb-${idx}`} fill="#2563eb" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          </div>

        </>
      )}

      {/* Month-wise & revenue (admin) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* MONTH-WISE LEADS (Jan - Dec) */}
        <div className="p-4 bg-white shadow rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-black">MONTH-WISE LEADS (Jan - Dec)</h3>
            <div className="text-sm text-slate-500">Total: {monthBuckets.totalCount}</div>
          </div>
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={monthCounts} margin={{ top: 10, right: 12, left: 0, bottom: 8 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(v) => [v, 'Leads']} />
                <Bar dataKey="count" barSize={28}>
                  {monthCounts.map((entry, idx) => <Cell key={`mcell-${idx}`} fill={COLORS[idx % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* TOTAL REVENUE (ADMIN ONLY) */}
        {role === 'ADMIN' && monthRevenueTrend && (
          <div className="col-span-1 p-5 border border-indigo-100 shadow-xl bg-gradient-to-br from-indigo-50 via-white to-indigo-100 rounded-2xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-black">TOTAL REVENUE</h3>
              <span className="text-sm font-medium text-slate-500">(Jan - Dec)</span>
            </div>

            <div className="relative flex items-baseline justify-center mb-3 mt-14">
              <span className="text-4xl font-extrabold tracking-tight text-indigo-700">
                ₹{monthRevenueTrend.total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </span>
              <span className="ml-2 text-sm font-bold text-slate-500">INR</span>
              <div className="absolute w-6 h-6 rounded-full right-6 bg-indigo-500/20 blur-md animate-pulse"></div>
            </div>

            <div className="relative w-full h-10 overflow-hidden rounded-sm bg-slate-200">
              <div
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-500 via-blue-500 to-pink-500 animate-[shimmer_2s_infinite]"
                style={{ width: `${Math.min(100, (monthRevenueTrend.total / 100000) * 100)}%`, transition: 'width 800ms ease' }}
              />
            </div>

            <div style={{ width: '100%', height: 100, marginTop: '10%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthRevenueTrend.trend} margin={{ top: 6, right: 8, left: 0, bottom: 6 }}>
                  <defs>
                    <linearGradient id="revArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                  <YAxis hide allowDecimals={false} />
                  <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                  <Area type="monotone" dataKey="value" stroke="#4f46e5" fill="url(#revArea)" strokeWidth={2} dot={{ r: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="flex items-center justify-end mt-3">
              <div className="text-xs italic text-slate-500">*Calculated from NegotiatedFee field</div>
            </div>
          </div>
        )}
      </div>

      {/* Lower charts (batch day / donut / pies) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Batch Day Chart */}
        <div className="p-6 bg-white shadow rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="mb-2 text-xl font-bold text-black">BATCH DAY DISTRIBUTION</h3>
            <div className="text-sm text-black">Total: <span className="font-semibold">{batchDayData.reduce((a, b) => a + b.count, 0)}</span></div>
          </div>
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={batchDayData} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
                <defs>
                  <linearGradient id="gradMorning" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="gradEvening" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f97316" stopOpacity={1} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="gradUnknown" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#94a3b8" stopOpacity={1} />
                    <stop offset="100%" stopColor="#64748b" stopOpacity={1} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="6 4" horizontal={true} vertical={false} strokeWidth={2} stroke="#cbd5e1" />
                <XAxis dataKey="name" type="category" axisLine={{ stroke: '#cbd5e1', strokeWidth: 3, strokeDasharray: '6 4' }} tickLine={false} tick={{ fill: '#475569', fontSize: 12, fontWeight: 500 }} padding={{ left: 20, right: 20 }} />
                <YAxis domain={[0, 9]} allowDecimals={false} axisLine={false} tickLine={false} />
                <Tooltip formatter={(value) => [value, 'Count']} wrapperStyle={{ borderRadius: 8 }} />
                <Bar dataKey="count" name="Count" radius={[0,0,0,0]} barSize={150} animationDuration={800}>
                  {batchDayData.map((entry, idx) => {
                    const id = entry.name === 'MORNING' ? 'gradMorning' : entry.name === 'EVENING' ? 'gradEvening' : 'gradUnknown';
                    return <Cell key={`cell-${idx}`} fill={`url(#${id})`} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(180deg,#60a5fa,#3b82f6)' }}></span>
              <span className="text-slate-600">Morning</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(180deg,#f97316,#ef4444)' }}></span>
              <span className="text-slate-600">Evening</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-slate-400"></span>
              <span className="text-slate-600">Unknown</span>
            </div>
          </div>
        </div>

        {/* Lead Category Donut */}
        <div className="col-span-1 p-4 bg-white shadow rounded-xl">
          <h3 className="mb-2 text-xl font-bold text-black">LEAD CATEGORY</h3>
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={typeBreakdown}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={80}
                  outerRadius={140}
                  paddingAngle={1}
                  label={(entry) => `${entry.name} (${entry.value})`}
                >
                  {typeBreakdown.map((entry, index) => {
                    let color = '#94a3b8';
                    if (entry.name === 'Students') color = '#0F059E';
                    else if (entry.name === 'Professionals') color = '#1B5349';
                    return <Cell key={`cell-${index}`} fill={color} />;
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-blue-800 rounded-full"></span>
              <span className="text-slate-600">Students</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-800 rounded-full"></span>
              <span className="text-slate-600">Professionals</span>
            </div>
          </div>
        </div>
      </div>

      {/* Grid for pie charts (call status, enrollment type) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* CALL STATUS */}
        <div className="col-span-1 p-4 bg-white shadow rounded-xl">
          <h3 className="mb-2 text-xl font-bold text-black">CALL STATUS</h3>
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={callStatusData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={80}
                  outerRadius={140}
                  paddingAngle={1}
                  label={(entry) => `${entry.name} (${entry.value})`}
                >
                  {callStatusData.map((entry, index) => {
                    let color = '#94a3b8';
                    if (entry.name === 'REQUIRED') color = '#721604';
                    else if (entry.name === 'PERFORMED') color = '#024519';
                    else if (entry.name === 'NOT REQUIRED') color = '#ef4444';
                    return <Cell key={`cell-${index}`} fill={color} />;
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-red-800 rounded-full"></span>
              <span className="text-slate-600">Required</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-800 rounded-full"></span>
              <span className="text-slate-600">Performed</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-red-500 rounded-full"></span>
              <span className="text-slate-600">Not Required</span>
            </div>
          </div>
        </div>

        {/* ENROLLMENT TYPE */}
        <div className="col-span-1 p-4 bg-white shadow rounded-xl">
          <h3 className="mb-2 text-xl font-bold text-black">ENROLLMENT TYPE</h3>
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={enrollmentTypeData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={5}
                  outerRadius={140}
                  paddingAngle={1}
                  label={(entry) => `${entry.name} (${entry.value})`}
                >
                  {enrollmentTypeData.map((entry, index) => {
                    let color = '#94a3b8';
                    if (entry.name === 'JUNIOR PENETRATION TESTER') color = '#1e3a8a';
                    else if (entry.name === 'SENIOR PENETRATION TESTER') color = '#064e3b';
                    return <Cell key={`cell-${index}`} fill={color} />;
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-red-900 rounded-full"></span>
              <span className="text-slate-600">Pending</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-blue-900 rounded-full"></span>
              <span className="text-slate-600">Junior Penetration Tester</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-900 rounded-full"></span>
              <span className="text-slate-600">Senior Penetration Tester</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment link + first installment */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="col-span-1 p-4 bg-white shadow rounded-xl">
          <h3 className="mb-2 text-xl font-bold text-black">PENDING PAYMENT LINKS TO SEND</h3>
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={paymentLinkData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={5}
                  outerRadius={140}
                  paddingAngle={1}
                  label={(entry) => `${entry.name} (${entry.value})`}
                >
                  {paymentLinkData.map((entry, index) => {
                    let color = '#94a3b8';
                    if (entry.name === 'PENDING') color = '#7f1d1d';
                    else if (entry.name === 'SENT') color = '#064e3b';
                    return <Cell key={`cell-${index}`} fill={color} />;
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-red-900 rounded-full"></span>
              <span className="text-slate-600">Pending</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-900 rounded-full"></span>
              <span className="text-slate-600">Sent</span>
            </div>
          </div>
        </div>

        <div className="col-span-1 p-4 bg-white shadow-lg rounded-2xl">
          <h3 className="mb-3 text-xl font-bold text-black">FIRST INSTALLMENT STATUS</h3>

          <div className="flex flex-col items-center gap-6 mt-14 sm:flex-row">
            {/* RADIAL */}
            <div className="flex items-center justify-center">
              <div className="relative h-60 w-60">
                <svg viewBox="0 0 36 36" className="w-full h-full">
                  <defs>
                    <linearGradient id="gPaid" x1="0" x2="1">
                      <stop offset="0%" stopColor="#34d399" />
                      <stop offset="100%" stopColor="#059669" />
                    </linearGradient>
                  </defs>

                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e6edf3" strokeWidth="4" />

                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="url(#gPaid)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={`${firstInstallmentData.paidPct} ${100 - firstInstallmentData.paidPct}`}
                    strokeDashoffset="25"
                    style={{ transition: 'stroke-dasharray 800ms ease' }}
                    aria-hidden="true"
                  />
                </svg>

                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-lg font-semibold text-slate-800">{firstInstallmentData.paidPct}%</div>
                  <div className="text-xs text-slate-400">PAID</div>

                  <div className="mt-2 text-center">
                    <div className="text-sm text-slate-600">Paid <span className="font-bold text-green-700">{firstInstallmentData.counts.PAID}</span></div>
                    <div className="text-sm text-slate-600">Total <span className="font-bold">{firstInstallmentData.total}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* INFO + MINI BARS */}
            <div className="flex-1 w-full">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm text-slate-500">Summary</div>
                  <div className="text-lg font-bold text-black">{firstInstallmentData.total} leads</div>
                </div>

                <div className="flex items-center gap-3">
                  <button className="px-3 py-2 text-sm font-semibold text-white transition bg-indigo-600 rounded-md shadow hover:bg-indigo-700" onClick={() => navigate('/first-installments')}>
                    Open Payments
                  </button>
                </div>
              </div>

              {/* Mini bar Paid */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-green-50">
                      <svg className="w-4 h-4 text-green-700" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293A1 1 0 103.293 10.707l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"/></svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700">Paid</div>
                      <div className="text-xs text-slate-400">{firstInstallmentData.counts.PAID} leads</div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-green-700">{firstInstallmentData.paidPct}%</div>
                </div>

                <div className="w-full h-12 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full" style={{ width: `${firstInstallmentData.paidPct}%`, background: 'linear-gradient(90deg,#34d399,#059669)', transition: 'width 700ms ease', boxShadow: 'inset 0 -2px 6px rgba(0,0,0,0.06)' }} />
                </div>
              </div>

              {/* Mini bar Unpaid */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-rose-50">
                      <svg className="w-4 h-4 text-rose-700" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11V5a1 1 0 10-2 0v2a1 1 0 00.293.707L10 9l.707-.293A1 1 0 0011 7zM9 12a1 1 0 012 0v3a1 1 0 11-2 0v-3z"/></svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700">Unpaid</div>
                      <div className="text-xs text-slate-400">{firstInstallmentData.counts.UNPAID} leads</div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-rose-700">{firstInstallmentData.unpaidPct}%</div>
                </div>

                <div className="w-full h-12 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full" style={{ width: `${firstInstallmentData.unpaidPct}%`, background: 'linear-gradient(90deg,#ff7a7a,#ef4444)', transition: 'width 700ms ease', boxShadow: 'inset 0 -2px 6px rgba(0,0,0,0.04)' }} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 mt-4 border-t">
            <div className="text-sm text-slate-500">Last updated: <span className="font-medium text-slate-700">just now</span></div>
            <div className="flex items-center gap-3">
              <button className="px-2 py-1 text-xs transition border rounded-md border-slate-200 text-slate-700 hover:bg-slate-50" onClick={() => window.location.reload()}>Refresh</button>
            </div>
          </div>
        </div>
      </div>

      {/* Enrolled status and joining messages */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="col-span-1 p-4 bg-white shadow-lg rounded-xl">
          <h3 className="mb-2 text-xl font-bold text-black">ENROLLED STATUS</h3>
          <div className="grid items-center grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm text-slate-500">Total</div>
                  <div className="text-2xl font-bold text-black">{enrolledStatusData.total}</div>
                </div>
                <div className="text-sm text-slate-500">Distribution</div>
              </div>

              <div className="relative w-full h-12 overflow-hidden border rounded-full bg-slate-100 border-slate-200">
                <div style={{ width: `${enrolledStatusData.pctNo}%`, background: 'linear-gradient(90deg,#ef4444,#dc2626)', height: '100%', float: 'left', transition: 'width 600ms ease' }} title={`No: ${enrolledStatusData.counts.NO} (${enrolledStatusData.pctNo}%)`} />
                <div style={{ width: `${enrolledStatusData.pctPending}%`, background: 'linear-gradient(90deg,#F93434,#720404)', height: '100%', float: 'left', transition: 'width 600ms ease' }} title={`Pending: ${enrolledStatusData.counts.PENDING} (${enrolledStatusData.pctPending}%)`} />
                <div style={{ width: `${enrolledStatusData.pctYes}%`, background: 'linear-gradient(90deg,#34d399,#10b981)', height: '100%', float: 'left', transition: 'width 600ms ease' }} title={`Yes: ${enrolledStatusData.counts.YES} (${enrolledStatusData.pctYes}%)`} />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="px-3 py-1 text-sm font-medium rounded-full bg-white/70">
                    {enrolledStatusData.counts.YES} Enrolled
                    <span className="mx-2 text-slate-400">/</span>
                    {enrolledStatusData.total} total
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-3 md:col-span-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-indigo-50">
                <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-md">
                  <svg className="w-5 h-5 text-indigo-600" viewBox="0 0 20 20" fill="currentColor"><path d="M2 5a2 2 0 012-2h3.5a1 1 0 010 2H4v10h12V5h-3.5a1 1 0 110-2H16a2 2 0 012 2v11a1 1 0 01-1 1H3a1 1 0 01-1-1V5z"/></svg>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Pending</div>
                  <div className="text-lg font-semibold text-red-700">{enrolledStatusData.counts.PENDING}</div>
                  <div className="text-xs text-slate-400">{enrolledStatusData.pctPending}%</div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50">
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-md">
                  <svg className="w-5 h-5 text-green-600" viewBox="0 0 20 20" fill="currentColor"><path d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293A1 1 0 103.293 10.707l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"/></svg>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Yes (Enrolled)</div>
                  <div className="text-lg font-semibold text-green-700">{enrolledStatusData.counts.YES}</div>
                  <div className="text-xs text-slate-400">{enrolledStatusData.pctYes}%</div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-50">
                <div className="flex items-center justify-center w-10 h-10 rounded-md bg-rose-100">
                  <svg className="w-5 h-5 text-rose-600" viewBox="0 0 20 20" fill="currentColor"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11V5a1 1 0 10-2 0v2a1 1 0 00.293.707L10 9l.707-.293A1 1 0 0011 7zM9 12a1 1 0 012 0v3a1 1 0 11-2 0v-3z"/></svg>
                </div>
                <div>
                  <div className="text-xs text-slate-500">No</div>
                  <div className="text-lg font-semibold text-rose-700">{enrolledStatusData.counts.NO}</div>
                  <div className="text-xs text-slate-400">{enrolledStatusData.pctNo}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-1 p-4 border shadow-xl bg-white/90 backdrop-blur-sm rounded-2xl border-slate-100">
          <h3 className="mb-3 text-lg font-semibold text-slate-800">JOINING MESSAGES TO BE SENT</h3>

          <div className="flex flex-col items-center gap-4 mt-6 md:flex-row">
            <div className="relative flex items-center justify-center w-44 h-44">
              <div style={{ width: 176, height: 176 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      <linearGradient id="jmGradPending" x1="0" x2="1">
                        <stop offset="0%" stopColor="#f97316" stopOpacity="1" />
                        <stop offset="100%" stopColor="#fb923c" stopOpacity="1" />
                      </linearGradient>
                      <linearGradient id="jmGradSent" x1="0" x2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity="1" />
                        <stop offset="100%" stopColor="#059669" stopOpacity="1" />
                      </linearGradient>
                    </defs>

                    <Pie data={joiningMessageElegant.series} dataKey="value" nameKey="name" innerRadius={56} outerRadius={84} cornerRadius={8} paddingAngle={4} labelLine={false} isAnimationActive={true}>
                      {joiningMessageElegant.series.map((entry, idx) => (
                        <Cell key={`jm-${idx}`} fill={entry.name === 'Pending' ? 'url(#jmGradPending)' : 'url(#jmGradSent)'} stroke="#ffffff" strokeWidth={1} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value}`, name]} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 6px 18px rgba(14,30,37,0.12)' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="absolute flex flex-col items-center justify-center text-center pointer-events-none" style={{ transform: 'translateY(-4px)' }}>
                <div className="text-2xl font-extrabold text-slate-800">{joiningMessageElegant.pct.sent}%</div>
                <div className="text-xs text-slate-500">SENT</div>
              </div>
            </div>

            <div className="flex-1 w-full">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs text-slate-400">Total leads</div>
                  <div className="text-xl font-semibold text-slate-800">{joiningMessageElegant.counts.total}</div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(90deg,#f97316,#fb923c)' }} />
                    <div className="text-sm text-slate-600">Pending <span className="ml-2 font-semibold text-slate-800">{joiningMessageElegant.counts.pending}</span></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(90deg,#34d399,#059669)' }} />
                    <div className="text-sm text-slate-600">Sent <span className="ml-2 font-semibold text-slate-800">{joiningMessageElegant.counts.sent}</span></div>
                  </div>
                </div>
              </div>

              <div className="w-full h-3 overflow-hidden border rounded-full bg-slate-100 border-slate-100">
                <div style={{ width: `${joiningMessageElegant.pct.sent}%`, height: '100%', background: 'linear-gradient(90deg,#34d399,#059669)', transition: 'width 700ms ease' }} />
              </div>

              <div className="mt-3 text-xs text-slate-500">Sent % shows the proportion of leads with a recorded joining message. Pending items are highlighted for follow-up.</div>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="text-red-500">{error}</div>}
    </div>
  );
}

export default DashboardFirstInstallment;
